import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@/ui/components/ui/button";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import {
  countStagedChanges,
  sameScope,
  useBulkSelectionStore,
  type SelectionScopeSignature,
} from "../../stores/useBulkSelectionStore";

interface ResumeSelectionBannerProps {
  /** The view the reviewer is looking at right now. */
  currentScope: SelectionScopeSignature;
  locationName?: string;
}

/** "Aug 7–9", or "Aug 7 – Sep 2" when the range crosses a month. */
const formatWindow = (
  fromIso: string,
  toIso: string,
  t: TFunction<"mlExports">,
): string => {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "";
  const month = (d: Date) => formatDate(d, "MMM");
  return month(from) === month(to) && from.getFullYear() === to.getFullYear()
    ? t("resumeBanner.windowSameMonth", {
        month: month(from),
        from: from.getDate(),
        to: to.getDate(),
      })
    : t("resumeBanner.windowRange", {
        fromMonth: month(from),
        from: from.getDate(),
        toMonth: month(to),
        to: to.getDate(),
      });
};

/**
 * Offers back a selection recovered from a previous session.
 *
 * Deliberately explicit rather than automatic: the saved ids mean "the frames
 * I had picked out", and silently re-selecting them under a different location
 * or time window would stage a bulk apply against images the reviewer never
 * saw. So the banner names the scope the selection came from, says plainly
 * when it doesn't match the current view, and makes the reviewer choose.
 *
 * One line by design (per the MIN-261 mock, Screen C): this interrupts someone
 * who came back to work, so it states what is on offer and gets out of the way.
 * The scope-mismatch warning is the one thing allowed to add a second line,
 * because acting on it wrongly is the expensive mistake.
 */
const ResumeSelectionBanner = ({
  currentScope,
  locationName,
}: ResumeSelectionBannerProps) => {
  const { t } = useTranslation("mlExports");
  const resumable = useBulkSelectionStore((s) => s.resumable);
  const liveIds = useBulkSelectionStore((s) => s.ids);
  const restore = useBulkSelectionStore((s) => s.restoreResumable);
  const discard = useBulkSelectionStore((s) => s.discardResumable);

  // Nothing to offer, or the reviewer has already started a fresh selection —
  // in which case interrupting them with old work would be noise.
  if (!resumable || liveIds.size > 0) return null;

  const matchesScope = sameScope(resumable.scope, currentScope);
  const staged = countStagedChanges({
    ids: new Set(resumable.ids),
    filterSnapshot: resumable.filterSnapshot,
    drafts: resumable.drafts,
    dumpImageValidOverride: resumable.dumpImageValidOverride,
    scope: resumable.scope,
    savedAt: resumable.savedAt,
    resumable: null,
  });

  const facts = [
    t("resumeBanner.imagesSelected", {
      count: resumable.ids.length,
      value: formatNumber(resumable.ids.length),
    }),
    locationName,
    resumable.scope
      ? formatWindow(resumable.scope.fromDate, resumable.scope.toDate, t)
      : null,
    resumable.savedAt > 0
      ? t("resumeBanner.savedAt", {
          relative: formatRelativeTimeUTC(
            new Date(resumable.savedAt).toISOString(),
          ),
        })
      : null,
    staged > 0
      ? t("resumeBanner.stagedChanges", {
          count: staged,
          value: formatNumber(staged),
        })
      : null,
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-2.5">
      <div className="flex items-center gap-3">
        <History className="h-4 w-4 shrink-0 text-primary" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold">{t("resumeBanner.title")}</span>{" "}
          <span className="text-muted-foreground">{facts.join(" · ")}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" onClick={restore}>
            {t("resumeBanner.restore")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={discard}
          >
            {t("resumeBanner.discard")}
          </Button>
        </div>
      </div>
      {!matchesScope && (
        <p className="mt-1.5 pl-7 text-xs text-amber-600 dark:text-amber-400">
          {t("resumeBanner.scopeMismatch")}
        </p>
      )}
    </div>
  );
};

export default ResumeSelectionBanner;
